import {
  Component,
  Input,
  OnInit,
  NgModule,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { Auth0Service } from 'src/app/auth/auth0.service';
import { UserService } from 'src/app/shared/services/user/user.service';
import {
  DxListModule,
  DxContextMenuModule,
  DxPopupModule,
  DxFormModule,
  DxValidationGroupModule,
  DxValidatorModule,
  DxButtonModule,
  DxScrollViewModule,
} from 'devextreme-angular';
import { CommonModule } from '@angular/common';
import { PipeModule } from 'src/app/pipe.module';
import { environment } from 'src/environments/environment';
import { DirectService } from 'src/app/shared/services/user/direct.service';
import { RegistrationService } from 'src/app/shared/services/user/registration.service';
import { RouterModule } from '@angular/router';
import { NotificationService } from '../../services/notification.service';
import { PosthogService } from '../../services/posthog.service';
import { FeatureFlagsService } from 'src/app/shared/services/featureFlags.service';

@Component({
  selector: 'app-pre-reg-terms',
  templateUrl: './pre-reg-terms.component.html',
  styleUrls: ['./pre-reg-terms.component.scss'],
})
export class PreRegTermsComponent implements OnInit {
  profile: any;
  userMetadata = environment.auth0.userMetadata;
  user: any;
  agreementsBoxVisible = false;

  agreement = {
    payment_option: '',
    agree: false,
    terms: null,
  };

  buttonSubmitOptions: any = {
    text: 'Submit',
    type: 'success',
    useSubmitBehavior: true,
    width: 300,
    disabled: true,
  };

  buttonCancelOptions: any = {
    text: 'Cancel',
    type: 'danger',
    width: 300,
    disabled: false,
  };

  termBlock: any;
  privacyBlocks: any;
  selectedPrivacyBlock: any;
  privacyVisible: boolean;
  hydraEnabled = environment.hydraEnabled;
  supplierCompanyName = environment.supplierConfig.supplierCompanyName;

  selectTermsOptions: any;
  terms: any;
  termsOptions: any;

  facilities: any[];

  client = null;

  loading = false;

  formColCount = 2; // Responsive column count for form
  popupWidth = '90%'; // Responsive popup width

  constructor(
    public auth0: Auth0Service,
    private userService: UserService,
    private directService: DirectService,
    private registrationService: RegistrationService,
    private notificationService: NotificationService,
    private posthogService: PosthogService,
    private featureFlagsService: FeatureFlagsService,
  ) {}

  ngOnInit(): void {
    this.getMe();
    this.getMsClient();
    this.buttonCancelOptions.onClick = () => this.onButtonCancelClick();
    this.termsOptions = {
      value: this.agreement.agree,
      onValueChanged: e => this.onAgreeChanged(e),
      text: 'I have verified my data and Agree to Terms',
    };
    this.updateFormLayout();
    window.addEventListener('resize', () => this.updateFormLayout());
  }

  updateFormLayout() {
    const width = window.innerWidth;

    if (width < 576) {
      // Small mobile
      this.formColCount = 1;
      this.popupWidth = '95%';
    } else if (width < 768) {
      // Tablet
      this.formColCount = 1;
      this.popupWidth = '90%';
    } else if (width < 1024) {
      // Small desktop
      this.formColCount = 2;
      this.popupWidth = '85%';
    } else {
      // Large desktop
      this.formColCount = 2;
      this.popupWidth = 'auto';
    }
  }

  getMe(refresh = false) {
    this.userService.getMe(refresh).subscribe(
      result => {
        this.user = result.data;
        this.validateUserCompany();
        if (this.agreementsBoxVisible) {
          this.getPharmacies();
          this.getDirectBlocks();
          this.getRegistrationData();
        }
      },
      () => this.auth0.logout(),
    );
  }

  onButtonCancelClick() {
    this.posthogService.capture('registration_terms', { status: 'declined' });
    this.registrationService
      .agreeToPreRegistrationIssueEmail()
      .subscribe(() => this.auth0.logout())
      .add(() => (this.loading = false));
  }

  getPharmacies() {
    this.userService.getPharmacies().subscribe(
      result => (this.facilities = result.data),
      () =>
        this.notificationService.showErrorMessage('Failed to get pharmacies'),
    );
  }

  validateUserCompany() {
    // Namespace-gated like the rest of MDG-176 (switch-facilities,
    // facility-card): no gate flag for this client, no terms wall. Absent
    // flags read as null, so a failed flag fetch hides the popup — same
    // fail-open convention as the server-side order gate.
    const gateActive =
      !!this.featureFlagsService.getByEnv('process', 'terms_acceptance_gate') ||
      !!this.featureFlagsService.getByEnv('process', 'soft_terms_acceptance_gate');
    if (!gateActive) {
      this.agreementsBoxVisible = false;
      return;
    }
    // Buyer-only wall: admins, sellers, and reps never accept buyer terms.
    if (this.user.is_admin || this.user.is_seller || this.user.is_representative) {
      this.agreementsBoxVisible = false;
      return;
    }
    // Must mirror the backend predicate company_terms_accepted():
    // agreed_to_terms AND user_agreed AND agreed_to_terms_date.
    this.agreementsBoxVisible = !(
      this.user.company.agreed_to_terms &&
      this.user.company.user_agreed &&
      this.user.company.agreed_to_terms_date
    );
  }

  onPopupShown() {
    this.posthogService.capture('registration_terms', { status: 'viewed' });
  }

  getDirectBlocks() {
    this.directService.getDirectClient$().subscribe(
      result => {
        this.termBlock = result.data.find(
          block => block.block_type === 'terms_of_use',
        );
        this.privacyBlocks = result.data.filter(
          block => block.block_type === 'privacy_policy',
        );
      },
      () => {},
    );
  }

  getRegistrationData() {
    this.registrationService.getRegistrationData().subscribe(result => {
      this.terms = result.data.gpo_choices.find(
        gpo => gpo.id === this.user.company.gpo_id,
      )?.user_terms.terms;
      this.setSelectTermsOptions();
    });
  }

  getMsClient() {
    this.userService.getMsClient$().subscribe(
      result => (this.client = result.data),
      () => {},
    );
  }

  get isAchTermsCompany(): boolean {
    const company = this.user?.company;
    // The serializer defaults a NULL payment_option to 'ach_check', so a real
    // ACH-terms customer is one that ALSO has mi_terms_days assigned.
    return !!company
      && (company.payment_option || '').startsWith('ach')
      && !!company.mi_terms_days;
  }

  setSelectTermsOptions() {
    if (this.isAchTermsCompany) {
      // ACH terms are assigned, not chosen — no selector; prefill the
      // agreement with the company's existing terms so Submit proceeds.
      this.agreement.payment_option = this.user.company.payment_option;
      this.agreement.terms = this.user.company.terms;
      return;
    }
    const valueExpr = this.hydraEnabled ? 'id' : 'rate';
    this.selectTermsOptions = {
      disabled: false,
      dataSource: this.terms,
      displayExpr: 'name',
      valueExpr,
      onValueChanged: e => this.onTermChanged(e),
    };
    if (this.terms?.length === 1) {
      this.selectTermsOptions.value = this.terms[0][valueExpr];
      this.onTermChanged({ value: this.terms[0][valueExpr] });
    }
  }

  onTermChanged(e) {
    if (e.value > 0) {
      const selectedTerms: any = this.terms.find(
        term => term[this.hydraEnabled ? 'id' : 'rate'] === e.value,
      );
      if (selectedTerms) {
        this.agreement.terms = selectedTerms.rate;
        this.agreement.payment_option = selectedTerms.payment_option;
      }
    } else {
      this.agreement.terms = e.value;
    }
  }

  onAgreeChanged(e) {
    this.buttonSubmitOptions = {
      text: 'Submit',
      type: 'success',
      useSubmitBehavior: true,
      width: 300,
      disabled: !e.value,
    };
  }

  onSubmit(e) {
    e.preventDefault();
    this.loading = true;
    this.registrationService
      .agreeToPreRegistration(this.agreement)
      .subscribe(
        () => {
          this.posthogService.capture('registration_terms', { status: 'accepted' });
          this.notificationService.showSuccessMessage('Registration Complete');
          this.getMe(true);
          this.agreementsBoxVisible = false;
        },
        () => this.notificationService.showErrorMessage('Registration Failed'),
      )
      .add(() => (this.loading = false));
  }

  navigateToUrl(url) {
    window.open(url, '_blank');
  }

  showPrivacyBlock(block) {
    if (!block.body) {
      window.open(block.url, '_blank');
      return;
    }
    this.selectedPrivacyBlock = block;
    this.privacyVisible = true;
  }
}

@NgModule({
  imports: [
    CommonModule,
    RouterModule,
    PipeModule,
    DxListModule,
    DxContextMenuModule,
    DxPopupModule,
    DxFormModule,
    DxValidationGroupModule,
    DxValidatorModule,
    DxButtonModule,
    DxScrollViewModule,
  ],
  declarations: [PreRegTermsComponent],
  exports: [PreRegTermsComponent],
})
export class PreRegTermsModule {}
